import { useState } from 'react';
import { Button, Cluster, Field, Input, Panel, StatusBadge } from '../../components';
import type { TaxCalculationResult, TaxReturnResource, VatCalculationRequest } from '../../lib/api';
import { formatKobo } from './invoice';

export interface BusinessVatFactsInput {
  period: string;
  taxableOutputNaira: string;
  zeroRatedOutputNaira: string;
  exemptOutputNaira: string;
  allowableInputVatNaira: string;
}

export function BusinessVatReturnInteraction({ busy = false, calculation, taxReturn, reviewedFactSnapshotSha256,
  filingActionsAvailable = false, onCalculate, onAction }: {
  busy?: boolean;
  calculation?: TaxCalculationResult;
  taxReturn?: TaxReturnResource;
  reviewedFactSnapshotSha256?: string;
  filingActionsAvailable?: boolean;
  onCalculate(values: BusinessVatFactsInput): Promise<void>;
  onAction(action: 'prepare' | 'review' | 'approve' | 'sign'): Promise<void>;
}) {
  const [values, setValues] = useState<BusinessVatFactsInput>({ period: '2026-07', taxableOutputNaira: '',
    zeroRatedOutputNaira: '0', exemptOutputNaira: '0', allowableInputVatNaira: '0' });
  const [error, setError] = useState<string>();
  const [dirty, setDirty] = useState(false);
  const update = (key: keyof BusinessVatFactsInput, value: string) => setValues((current) => ({ ...current, [key]: value }));
  return <Panel className="am-b1-invoice-builder" id="return-facts" tabIndex={-1} title="Prepare your VAT return"
    description="Confirm the reviewed monthly totals below to calculate a VAT estimate. Amoye will clearly tell you when return preparation and filing become available.">
    <form data-amoye-unsaved={dirty ? 'true' : 'false'} onChange={() => setDirty(true)}
      onSubmit={async (event) => { event.preventDefault(); setError(undefined);
      try { await onCalculate(values); setDirty(false); }
      catch (caught) { setError(caught instanceof Error ? caught.message : 'VAT calculation failed.'); } }}>
      <div className="am-b1-form-grid">
        <Field label="Return month" required><Input required type="month" value={values.period}
          onChange={(event) => update('period', event.target.value)} /></Field>
        <MoneyField label="Taxable output value (naira)" value={values.taxableOutputNaira} onChange={(value) => update('taxableOutputNaira', value)} />
        <MoneyField label="Zero-rated output value (naira)" value={values.zeroRatedOutputNaira} onChange={(value) => update('zeroRatedOutputNaira', value)} />
        <MoneyField label="Exempt output value (naira)" value={values.exemptOutputNaira} onChange={(value) => update('exemptOutputNaira', value)} />
        <MoneyField label="Allowable input VAT (naira)" value={values.allowableInputVatNaira} onChange={(value) => update('allowableInputVatNaira', value)} />
      </div>
      <label className="am-b1-review-confirmation"><input required type="checkbox" />
        <span>I have reviewed these VAT facts and understand that the result remains a draft until the required declaration and filing steps are completed.</span></label>
      {error && <p className="am-b1-invoice-error" role="alert">{error}</p>}
      <Button loading={busy} type="submit">Calculate VAT estimate</Button>
    </form>
    {calculation && <section aria-label="VAT calculation estimate" className="am-b1-artifact">
      <div><span>Estimated VAT payable</span><strong>{formatKobo(calculation.liabilityKobo)}</strong></div>
      <details><summary>View calculation audit details</summary><div><span>Calculation rules</span><code>{calculation.packageId} · {calculation.packageVersion}</code></div><div><span>Reviewed records reference</span><code>{reviewedFactSnapshotSha256 ?? calculation.inputSnapshotSha256}</code></div><div><span>Calculation reference</span><code>{calculation.outputSha256}</code></div></details>
      {taxReturn && <div><span>Return state</span><StatusBadge tone={taxReturn.status === 'SUBMISSION_REQUESTED' ? 'info' : 'warning'}>
        {taxReturn.status.replaceAll('_', ' ').toLowerCase()}</StatusBadge></div>
      }{taxReturn && <Cluster>
        <Button disabled={!filingActionsAvailable || taxReturn.status !== 'CALCULATED' || busy} onClick={() => onAction('prepare')} size="small">Prepare return</Button>
        <Button disabled={!filingActionsAvailable || taxReturn.status !== 'PREPARED' || busy} onClick={() => onAction('review')} size="small">Review return</Button>
        <Button disabled={!filingActionsAvailable || taxReturn.status !== 'REVIEWED' || busy} onClick={() => onAction('approve')} size="small">Approve return</Button>
        <Button disabled={!filingActionsAvailable || !['CALCULATED', 'PREPARED', 'APPROVED'].includes(taxReturn.status) || busy} onClick={() => onAction('sign')} size="small">Sign declaration</Button>
        <Button disabled size="small">Filing unavailable</Button>
      </Cluster>}
      <p>This estimate cannot yet be prepared or filed. Amoye is waiting for professional approval of the calculation rules and activation of the NRS filing connection.</p>
    </section>}
  </Panel>;
}

export async function bindBusinessVatFacts(values: BusinessVatFactsInput, identity: { taxpayerId: string; engagementId?: string }): Promise<{ request: VatCalculationRequest;
  periodEndExclusive: string }> {
  if (!/^\d{4}-(?:0[1-9]|1[0-2])$/.test(values.period)) throw new Error('Select a valid return month.');
  const periodStart = `${values.period}-01`;
  const facts = { kind: 'VAT' as const, taxableOutputValueKobo: vatNairaToKoboExact(values.taxableOutputNaira),
    zeroRatedOutputValueKobo: vatNairaToKoboExact(values.zeroRatedOutputNaira), exemptOutputValueKobo: vatNairaToKoboExact(values.exemptOutputNaira),
    allowableInputVatKobo: vatNairaToKoboExact(values.allowableInputVatNaira) };
  const packageId = 'AMOYE-VAT-NRS-MONTHLY-CAND-001' as const;
  const reviewedFactSnapshotSha256 = await sha256(stableJson({ schemaVersion: 'amoye-reviewed-business-vat-facts-1',
    taxpayerId: identity.taxpayerId, ...(identity.engagementId ? { engagementId: identity.engagementId } : {}),
    packageId, periodStart, facts }));
  return { request: { packageId, periodStart,
    reviewedFactSnapshotSha256, facts }, periodEndExclusive: nextMonth(periodStart) };
}

function MoneyField({ label, value, onChange }: { label: string; value: string; onChange(value: string): void }) {
  return <Field label={label} hint="No commas; at most two decimal places." required><Input inputMode="decimal"
    pattern="(0|[1-9][0-9]*)([.][0-9]{1,2})?" required value={value} onChange={(event) => onChange(event.target.value)} /></Field>;
}

export function vatNairaToKoboExact(value: string): string {
  if (!/^(0|[1-9]\d*)(\.\d{1,2})?$/.test(value)) throw new Error('Enter each amount as naira with no more than two decimal places.');
  const [whole = '0', fraction = ''] = value.split('.');
  return (BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'))).toString();
}

function nextMonth(periodStart: string): string {
  const year = Number(periodStart.slice(0, 4)); const month = Number(periodStart.slice(5, 7));
  const nextYear = month === 12 ? year + 1 : year; const next = month === 12 ? 1 : month + 1;
  return `${nextYear.toString().padStart(4, '0')}-${next.toString().padStart(2, '0')}-01`;
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object') return `{${Object.entries(value).sort(([a], [b]) => a.localeCompare(b))
    .map(([key, entry]) => `${JSON.stringify(key)}:${stableJson(entry)}`).join(',')}}`;
  return JSON.stringify(value);
}
